import type { Calendar } from "@/components/calendars/api.ts";
import type { CalendarEvent } from "@/types/events.ts";
import { isOkthxCalendarId } from "@/types/calendarEvent.ts";
import { isReminderBlob, isReminderEvent } from "@/shared/reminder.ts";
import { canEditEventInSidebar, canRsvpOnCalendar } from "@/shared/canEditEvent.ts";

// Re-exported so this module stays the one place calendar code asks "may I edit
// this, and is it a reminder?" — the predicates themselves are shared with
// native, which needs the same answer.
export {
  isOkthxCalendarId,
  isReminderBlob,
  isReminderEvent,
  canEditEventInSidebar,
  canRsvpOnCalendar,
};

/**
 * Two predicates with deliberately different scopes:
 *
 * - `canEditEventInSidebar(event)` mirrors the legacy inline check that lived
 *   in `EventEditSidebar.tsx`. It only inspects the event itself — organizer
 *   role, guestsCanModify flag, externalEventId presence, cancellation status.
 *   Sidebar permission behavior for provider events must stay byte-for-byte
 *   unchanged after the drag-to-reschedule refactor; okthx reminders take an
 *   earlier branch, since none of those provider-shaped tests describe them.
 *   It lives in `shared/client/` because native asks the same question.
 *
 * - `canDragEvent(event, calendarsById)` is strictly tighter. It layers two
 *   additional gates on top of the sidebar check:
 *     1. The owning calendar's `accessRole` must be "owner" or "writer"
 *        (read-only connector calendars block drag even if the event metadata
 *        looks editable — Google would 403 anyway).
 *     2. `pendingSyncUpdate` must be falsy (another change is already in
 *        flight; dragging would race the previous PATCH).
 *
 * `canDragEvent` delegates to `canEditEventInSidebar` for the event-level
 * checks, then layers on the two extra gates above. It stays web-only: native
 * has no drag-to-move for any event, and the calendar type it reads is web's.
 */

const WRITABLE_ACCESS_ROLES = new Set(["owner", "writer"]);

/**
 * Drag predicate. Adds calendar-writability and pending-sync checks on top of
 * the event-level predicate. Used by `useEventDrag` to decide whether
 * FullCalendar should even initiate the drag (`eventAllow`).
 */
export function canDragEvent(
  event: CalendarEvent,
  calendarsById: Map<string, Calendar> | undefined,
): boolean {
  if (event.pendingSyncUpdate) return false;
  if (!canEditEventInSidebar(event)) return false;

  // Calendar writability — if calendars haven't loaded yet, fall through to
  // "not draggable" rather than guessing. This is the safer default while
  // the calendar list query is still in flight.
  if (!calendarsById) return false;
  const calendar = calendarsById.get(event.calendarId);
  if (!calendar) return false;
  if (!calendar.accessRole) return false;
  return WRITABLE_ACCESS_ROLES.has(calendar.accessRole);
}

/**
 * Whether an event may be moved onto `targetCalendarId`. okthx calendars (e.g.
 * reminders) are closed to cross-calendar moves in both directions: an okthx
 * event cannot be dragged off onto a provider calendar, and a provider event
 * cannot be dragged onto an okthx calendar. Same-calendar moves (e.g. dragging a
 * reminder to a new time within okthx) are unaffected — only a calendar *change*
 * is gated.
 */
export function canMoveEventToCalendar(event: CalendarEvent, targetCalendarId: string): boolean {
  if (event.calendarId === targetCalendarId) return true;
  return !isOkthxCalendarId(event.calendarId) && !isOkthxCalendarId(targetCalendarId);
}
